import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Gift, Copy, Share2, Check, Users, Store, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';
import { UserContext, API } from '@/App';

const ReferralDetailsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [copied, setCopied] = useState(false);
  
  // Contact details state
  const [contactDetails, setContactDetails] = useState({
    mobile_number: '',
    whatsapp_number: '',
    email: ''
  });
  const [sameAsMobile, setSameAsMobile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Generate referral code from user phone or id
  const referralCode = user?.phone 
    ? `FOOD${user.phone.slice(-4)}` 
    : `FOOD${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
  
  const referralLink = `${process.env.REACT_APP_REFERRAL_DOMAIN || window.location.origin}/r/${referralCode}`;
  
  // Fetch existing contact details on load
  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        const response = await axios.get(`${API}/referral/contact-details`, { withCredentials: true });
        if (response.data) {
          setContactDetails({
            mobile_number: response.data.mobile_number || '',
            whatsapp_number: response.data.whatsapp_number || '',
            email: response.data.email || ''
          });
          // Check if whatsapp same as mobile
          if (response.data.mobile_number && response.data.mobile_number === response.data.whatsapp_number) {
            setSameAsMobile(true);
          }
        }
      } catch (err) {
        // No existing data or not logged in
      }
    };
    fetchContactDetails();
  }, []);
  
  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleShare = async () => {
    const shareData = {
      title: 'Join FoodMatrix!',
      text: `Use my referral code ${referralCode} to get rewards on FoodMatrix!`,
      url: referralLink
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        handleCopyCode();
      }
    } else {
      handleCopyCode();
    }
  };
  
  const handleMobileChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setContactDetails(prev => ({
      ...prev,
      mobile_number: digitsOnly,
      whatsapp_number: sameAsMobile ? digitsOnly : prev.whatsapp_number
    }));
    setErrors(prev => ({ ...prev, mobile_number: '' }));
  };
  
  const handleWhatsappChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setContactDetails(prev => ({
      ...prev,
      whatsapp_number: digitsOnly
    }));
  };
  
  const handleSameAsMobileChange = (checked) => {
    setSameAsMobile(checked);
    if (checked) {
      setContactDetails(prev => ({
        ...prev,
        whatsapp_number: prev.mobile_number
      }));
    }
  };
  
  const validateEmail = (email) => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const handleSaveDetails = async () => {
    // Validate
    const newErrors = {};
    
    if (!contactDetails.mobile_number) {
      newErrors.mobile_number = 'Mobile number is required';
    } else if (contactDetails.mobile_number.length !== 10) {
      newErrors.mobile_number = 'Mobile number must be 10 digits';
    }
    
    if (contactDetails.email && !validateEmail(contactDetails.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setSaving(true);
    
    try {
      await axios.post(`${API}/referral/contact-details`, contactDetails, { withCredentials: true });
      toast.success('Referral contact details saved successfully.');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Please login to save your details');
      } else {
        toast.error('Failed to save details. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Your Referral</h1>
              <p className="text-green-100 text-sm">Share and earn rewards</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Section 1: Referral Code Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 rounded-full">
              <Gift className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Your Referral Code</h2>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 text-center mb-4">
            <p className="text-3xl font-bold tracking-widest text-green-700" data-testid="referral-code">
              {referralCode}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button 
              onClick={handleCopyCode}
              className="flex-1 bg-white border-2 border-green-500 text-green-600 hover:bg-green-50 px-4 py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
              data-testid="copy-code-btn"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              {copied ? 'Copied!' : 'Copy Code'}
            </Button>
            <Button 
              onClick={handleShare}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-4 py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5" />
              Share
            </Button>
          </div>
        </div>

        {/* Section 2: Referral Contact Details */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800">Referral Contact Details</h2>
            <p className="text-sm text-gray-500 mt-1">Provide your contact details so referrals can reach you easily.</p>
          </div>
          
          {/* Desktop: 3 columns, Mobile: stacked */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <Input
                type="tel"
                placeholder="Enter 10-digit number"
                value={contactDetails.mobile_number}
                onChange={(e) => handleMobileChange(e.target.value)}
                maxLength={10}
                className={errors.mobile_number ? 'border-red-500' : ''}
                data-testid="referral-mobile"
              />
              {errors.mobile_number && (
                <p className="text-red-500 text-xs mt-1">{errors.mobile_number}</p>
              )}
            </div>
            
            {/* WhatsApp Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp Number
              </label>
              <Input
                type="tel"
                placeholder="Enter 10-digit number"
                value={contactDetails.whatsapp_number}
                onChange={(e) => handleWhatsappChange(e.target.value)}
                maxLength={10}
                disabled={sameAsMobile}
                className={sameAsMobile ? 'bg-gray-100' : ''}
                data-testid="referral-whatsapp"
              />
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="same-as-mobile"
                  checked={sameAsMobile}
                  onChange={(e) => handleSameAsMobileChange(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="same-as-mobile" className="text-xs text-gray-600">
                  Same as Mobile Number
                </label>
              </div>
            </div>
            
            {/* Email ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email ID <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={contactDetails.email}
                onChange={(e) => {
                  setContactDetails(prev => ({ ...prev, email: e.target.value }));
                  setErrors(prev => ({ ...prev, email: '' }));
                }}
                className={errors.email ? 'border-red-500' : ''}
                data-testid="referral-email"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
          </div>
          
          <Button
            onClick={handleSaveDetails}
            disabled={saving}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold"
            data-testid="save-details-btn"
          >
            {saving ? 'Saving...' : 'Save Details'}
          </Button>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">How It Works</h3>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold">1</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Share your code</p>
                <p className="text-sm text-gray-600">Send your referral code to friends, restaurants, or delivery partners</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold">2</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">They sign up</p>
                <p className="text-sm text-gray-600">Your referral signs up using your code</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold">3</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Earn rewards</p>
                <p className="text-sm text-gray-600">Get your reward after they complete their first action</p>
              </div>
            </div>
          </div>
        </div>

        {/* Rewards Summary */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Rewards</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-green-600" />
                <span className="text-gray-700">Refer a Friend</span>
              </div>
              <span className="font-bold text-green-600">₹100</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Store className="w-5 h-5 text-emerald-600" />
                <span className="text-gray-700">Refer a Restaurant</span>
              </div>
              <span className="font-bold text-emerald-600">₹150</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-teal-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-teal-600" />
                <span className="text-gray-700">Refer a Delivery Partner</span>
              </div>
              <span className="font-bold text-teal-600">₹150</span>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ReferralDetailsPage;
